import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";
import {
  FaUserPlus,
  FaEnvelope,
  FaLock,
  FaUserShield,
  FaSpinner,
  FaUsers,
  FaCalendarAlt,
  FaPhoneAlt,
  FaCheckCircle,
  FaUserAlt
} from "react-icons/fa";

const CreateStaff = () => {
  const { user } = useSelector((state) => state.auth);

  const [roles, setRoles] = useState([]);
  const [staff, setStaff] = useState([]);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    phone: "",
    role: "",
  });
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingStaff, setLoadingStaff] = useState(true);

  // Get auth headers
  const getHeaders = () => {
    const token = localStorage.getItem("token") || (user && user.token);
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // Fetch staff list
  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/staff`, getHeaders());
      if (res.data.success) {
        setStaff(res.data.data);
      }
    } catch (err) {
      toast.error("Failed to load staff list");
    } finally {
      setLoadingStaff(false);
    }
  };

  // Fetch custom roles and staff list on mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/roles`, getHeaders());
        if (res.data.success) {
          setRoles(res.data.data);
        }
      } catch (err) {
        toast.error("Failed to load roles");
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
    fetchStaff();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { email, password, phone, role } = formData;

    if (!email || !password || !phone) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!/^[0-9]{10}$/.test(phone.trim())) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    if (!role || role.trim() === "") {
      toast.error("Please select a role for the staff member");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/admin/create-staff`, formData, getHeaders());
      if (res.data.success) {
        toast.success(res.data.message || "Staff user created successfully!");
        setFormData({
          email: "",
          password: "",
          phone: "",
          role: "",
        });
        fetchStaff(); // Refresh staff list immediately
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create staff user");
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role?.toLowerCase()) {
      case "superadmin":
        return "bg-purple-50 text-purple-700 border-purple-200";
      case "admin":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "admin-staff":
        return "bg-indigo-50 text-indigo-700 border-indigo-200";
      case "staff":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      default:
        return "bg-slate-50 text-slate-700 border-slate-200";
    }
  };

  return (
    <div className="w-full py-8">
      <div className="px-4 sm:px-6 md:px-8 max-w-full mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Create Form */}
          <div className="lg:col-span-5 bg-white border border-gray-100 rounded-2xl shadow-sm p-6 sm:p-8">
            <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
              <FaUserAlt className="text-blue-500 text-sm" /> Account Information
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Email Address
                </label>
                <div className="relative border border-gray-200 focus-within:border-blue-500 rounded-xl transition-all duration-200 bg-gray-50/50">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                    <FaEnvelope />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-11 pr-4 py-3 bg-transparent text-sm font-semibold text-gray-800 placeholder-gray-400 focus:outline-none"
                    placeholder="e.g. staff@cabzigo.com"
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Password
                </label>
                <div className="relative border border-gray-200 focus-within:border-blue-500 rounded-xl transition-all duration-200 bg-gray-50/50">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                    <FaLock />
                  </div>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-11 pr-4 py-3 bg-transparent text-sm font-semibold text-gray-800 placeholder-gray-400 focus:outline-none"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              {/* Phone Field */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Phone Number
                </label>
                <div className="relative border border-gray-200 focus-within:border-blue-500 rounded-xl transition-all duration-200 bg-gray-50/50">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                    <FaPhoneAlt />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full pl-11 pr-4 py-3 bg-transparent text-sm font-semibold text-gray-800 placeholder-gray-400 focus:outline-none"
                    placeholder="e.g. 9876543210"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Assign Role
                </label>
                <div className="relative border border-gray-200 focus-within:border-blue-500 rounded-xl transition-all duration-200 bg-gray-50/50">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                    <FaUserShield />
                  </div>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full pl-11 pr-4 py-3 bg-transparent text-sm font-semibold text-gray-700 focus:outline-none"
                    required
                  >
                    <option value="" disabled>Select a role...</option>
                    <option value="admin">Admin</option>
                    {roles.map((r) => (
                      <option key={r._id} value={r.name}>
                        {r.name.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                {loadingRoles && (
                  <p className="text-[10px] text-gray-400 mt-1 italic flex items-center gap-1.5">
                    <FaSpinner className="animate-spin text-blue-500" /> Loading custom roles...
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm tracking-wide shadow-lg shadow-blue-200 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin" /> CREATING STAFF...
                  </>
                ) : (
                  "CREATE ACCOUNT"
                )}
              </button>
            </form>
          </div>

          {/* Right Column: Staff Directory Table */}
          <div className="lg:col-span-7 bg-white border border-gray-100 rounded-2xl shadow-sm p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FaUsers className="text-blue-500" /> Staff Directory
              </h2>
              <span className="text-xs bg-blue-50 text-blue-600 px-2.5 py-1 rounded-full font-bold border border-blue-100">
                {staff.length} Active Staff
              </span>
            </div>

            {loadingStaff ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
                <FaSpinner className="animate-spin text-blue-500 text-3xl" />
                <span className="text-sm font-semibold">Loading staff members...</span>
              </div>
            ) : staff.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 border border-dashed border-gray-200 rounded-xl text-gray-400 gap-2">
                <FaUsers className="text-4xl text-gray-300" />
                <span className="text-sm font-semibold text-gray-500">No staff members found</span>
                <span className="text-xs text-gray-400">Create an account on the left to get started.</span>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead>
                      <tr className="bg-gray-50/70">
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider rounded-l-xl">
                          Staff Details
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                          Assigned Role
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider rounded-r-xl">
                          Registered
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {staff.map((s) => (
                        <tr key={s._id} className="hover:bg-gray-50/40 transition-colors duration-150">
                          {/* Details */}
                          <td className="px-4 py-4.5 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold text-gray-800">
                                {s.email}
                              </span>
                              {s.phone && (
                                <span className="text-xs text-gray-400 flex items-center gap-1 mt-0.5 font-medium">
                                  <FaPhoneAlt className="text-[10px]" /> {s.phone}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Role */}
                          <td className="px-4 py-4.5 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getRoleBadgeClass(s.role)}`}>
                              <FaUserShield className="text-[10px] mr-1" /> {s.role?.toUpperCase()}
                            </span>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-4.5 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600">
                              <FaCheckCircle className="text-xs" /> {s.status?.toUpperCase() || "ACTIVE"}
                            </span>
                          </td>

                          {/* Registered At */}
                          <td className="px-4 py-4.5 whitespace-nowrap text-sm text-gray-500">
                            <span className="flex items-center gap-1.5 text-xs font-medium text-gray-400">
                              <FaCalendarAlt />
                              {new Date(s.createdAt).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateStaff;
